import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaGithub, FaDiscord, FaTwitter, FaHeart, FaInstagram, FaYoutube, FaFacebook } from 'react-icons/fa';
import { getApiUrl, API_ENDPOINTS } from '../config/apiConfig';

const FooterContainer = styled.footer`
  background: rgba(11, 6, 20, 0.9);
  border-top: 1px solid var(--border-color);
  padding: 60px 5% 30px;
  position: relative;
  z-index: 1;
`;

const FooterContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr;
  gap: 40px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 30px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 25px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const BrandSection = styled(FooterSection)`
  @media (max-width: 1024px) {
    grid-column: 1 / -1;
    margin-bottom: 20px;
  }
`;

const Logo = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  background: var(--gradient-pink);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 15px;
`;

const Description = styled.p`
  color: var(--secondary-color);
  line-height: 1.6;
  font-size: 0.9rem;
  margin-bottom: 20px;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 15px;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--glass-bg);
  border: 1px solid var(--border-color);
  color: var(--secondary-color);
  font-size: 1.1rem;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--gradient-pink);
    color: var(--gradient-pink);
    transform: translateY(-2px);
  }
`;

const SectionTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--primary-color);
  margin-bottom: 15px;
`;

const FooterLink = styled(Link)`
  color: var(--secondary-color);
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.3s ease;
  padding: 5px 0;

  &:hover {
    color: var(--primary-color);
  }
`;

const ExternalLink = styled.a`
  color: var(--secondary-color);
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.3s ease;
  padding: 5px 0;

  &:hover {
    color: var(--primary-color);
  }
`;

const Copyright = styled.div`
  margin-top: 40px;
  padding-top: 30px;
  border-top: 1px solid var(--border-color);
  text-align: center;
  color: var(--secondary-color);
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const Footer = () => {
  const [footerSettings, setFooterSettings] = useState({
    footer_logo: 'CineGlass',
    footer_description: 'En yeni filmleri, dizileri ve animeleri HD kalitede izleyin. Türkçe dublaj ve altyazı seçenekleriyle zengin içerik kütüphanesi.',
    footer_copyright: '© 2025 CineGlass',
    footer_copyright_text: 'Tüm hakları saklıdır.',
    footer_made_with: 'Made with ❤️ by CineGlass Team',
    social_github: '',
    social_discord: '',
    social_twitter: '',
    social_instagram: '',
    social_youtube: '',
    social_facebook: ''
  });

  useEffect(() => {
    const loadFooterSettings = async () => {
      try {
        const response = await fetch(getApiUrl(API_ENDPOINTS.GET_PUBLIC_SETTINGS));
        if (response.ok) {
          const data = await response.json();
          // Footer ayarlarını filtrele
          const footerData = Object.keys(data)
            .filter(key => key.startsWith('footer_') || key.startsWith('social_'))
            .reduce((obj, key) => {
              obj[key] = data[key] || footerSettings[key];
              return obj;
            }, {});
          
          setFooterSettings(prev => ({ ...prev, ...footerData }));
        }
      } catch (error) {
        console.error('Footer ayarları yüklenirken hata:', error);
      }
    };

    loadFooterSettings();
  }, []);

  const getSocialIcon = (platform) => {
    switch(platform) {
      case 'github': return <FaGithub />;
      case 'discord': return <FaDiscord />;
      case 'twitter': return <FaTwitter />;
      case 'instagram': return <FaInstagram />;
      case 'youtube': return <FaYoutube />;
      case 'facebook': return <FaFacebook />;
      default: return null;
    }
  };

  const socialPlatforms = ['github', 'discord', 'twitter', 'instagram', 'youtube', 'facebook'];
  const activeSocials = socialPlatforms.filter(platform => 
    footerSettings[`social_${platform}`] && footerSettings[`social_${platform}`].trim() !== ''
  );

  return (
    <FooterContainer>
      <FooterContent>
        <BrandSection>
          <Logo>{footerSettings.footer_logo}</Logo>
          <Description>{footerSettings.footer_description}</Description>
          <div>
            <strong style={{ color: 'var(--primary-color)', fontSize: '0.9rem' }}>
              {footerSettings.footer_copyright}
            </strong>
            <br />
            <span style={{ fontSize: '0.8rem', color: 'var(--secondary-color)' }}>
              {footerSettings.footer_copyright_text}
            </span>
          </div>
          {activeSocials.length > 0 && (
            <SocialLinks>
              {activeSocials.map(platform => (
                <SocialLink 
                  key={platform}
                  href={footerSettings[`social_${platform}`]} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  {getSocialIcon(platform)}
                </SocialLink>
              ))}
            </SocialLinks>
          )}
        </BrandSection>

        <FooterSection>
          <SectionTitle>Hakkımızda</SectionTitle>
          <FooterLink to="/about">Biz Kimiz?</FooterLink>
          <FooterLink to="/contact">İletişim</FooterLink>
        </FooterSection>

        <FooterSection>
          <SectionTitle>Kategoriler</SectionTitle>
          <FooterLink to="/kesfet">Keşfet</FooterLink>
          <FooterLink to="/takvim">Takvim</FooterLink>
          <FooterLink to="/tum-bolumler">Tüm Bölümler</FooterLink>
        </FooterSection>

        <FooterSection>
          <SectionTitle>Kaynaklar</SectionTitle>
          <ExternalLink href="https://www.themoviedb.org/" target="_blank" rel="noopener noreferrer">
            TMDB API
          </ExternalLink>
        </FooterSection>

        <FooterSection>
          <SectionTitle>Hukuki</SectionTitle>
          <FooterLink to="/privacy">Gizlilik Politikası</FooterLink>
          <FooterLink to="/cookie-policy">Çerez Politikası</FooterLink>
        </FooterSection>

        <FooterSection>
          <SectionTitle>Yardım</SectionTitle>
          <FooterLink to="/support">Destek</FooterLink>
        </FooterSection>
      </FooterContent>

      <Copyright>
        {footerSettings.footer_made_with.includes('❤️') ? (
          <>
            {footerSettings.footer_made_with.split('❤️')[0]}
            <FaHeart color="var(--gradient-pink)" />
            {footerSettings.footer_made_with.split('❤️')[1]}
          </>
        ) : (
          footerSettings.footer_made_with
        )}
      </Copyright>
    </FooterContainer>
  );
};

export default Footer;
